import React, { useState, useEffect } from "react";
import { CrossCheckCard } from "../components/CrossCheckCard";
import { api, API_BASE } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { EPCBadge } from "../components/EPCBadge";
import { FileText, Loader2, Download, AlertCircle, Search, Plus, Trash2, Target, PoundSterling, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";

const SCOPES = [
  { value: "d_only", label: "Band D only", description: "Not in Asana (incl. Y2 Standby) · heritage clear" },
  { value: "e_or_below", label: "Band E or below", description: "Not in Asana (incl. Y2 Standby) · heritage clear" },
  { value: "heritage_check", label: "Heritage check only (D-G)", description: "Listed / Conservation / Article 4 / AONB · not in Asana (incl. Y2 Standby)" },
  { value: "expired_below_c", label: "Expired EPC AND below band C", description: "Highest-priority funding candidates" },
  { value: "below_c", label: "Below band C (any expiry)", description: "Whole-stock C-uplift programme" },
  { value: "expired", label: "Expired EPC (any band)", description: "Re-assessment / re-lodgement priority" },
];

const GBP = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

export default function ProgrammePage() {
  const [scope, setScope] = useState("d_only");
  const [topN, setTopN] = useState(50);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api.get("/reports/programme-scope-counts").then(({ data }) => setCounts(data)).catch(() => {});
  }, []);
  const [generating, setGenerating] = useState(false);
  const [generatingCsv, setGeneratingCsv] = useState(false);
  const [preview, setPreview] = useState(null);

  const download = async (kind /* 'pdf' | 'csv' */) => {
    const isPdf = kind === "pdf";
    if (isPdf) setGenerating(true); else setGeneratingCsv(true);
    setPreview(null);
    try {
      const token = localStorage.getItem("epc_token");
      const url = isPdf
        ? `${API_BASE}/reports/retrofit-programme?top_n=${topN}&scope=${scope}`
        : `${API_BASE}/reports/retrofit-programme.csv?top_n=${topN}&scope=${scope}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      const contentType = res.headers.get("content-type") || "";
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const e = contentType.includes("json") ? await res.json() : { detail: await res.text() };
          detail = e.detail || detail;
        } catch (_) { /* noop */ }
        throw new Error(detail);
      }
      const propCount = res.headers.get("x-property-count");
      const portfolioTotal = res.headers.get("x-portfolio-total");
      const blob = await res.blob();
      const fileUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = `Retrofit-Programme-${scope}-${new Date().toISOString().slice(0, 10)}.${kind}`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(fileUrl); }, 500);
      setPreview({
        propCount: Number(propCount),
        portfolioTotal: portfolioTotal != null ? Number(portfolioTotal) : null,
        size: blob.size,
        kind,
      });
      toast.success(`${kind.toUpperCase()} ready · ${propCount} properties${portfolioTotal ? ` · ${GBP(portfolioTotal)}` : ""}`);
    } catch (e) {
      toast.error(`Programme ${kind.toUpperCase()} generation failed`, { description: e.message });
    } finally {
      if (isPdf) setGenerating(false); else setGeneratingCsv(false);
    }
  };
  const generate = () => download("pdf");
  const generateCsv = () => download("csv");

  return (
    <div className="p-8 space-y-6" data-testid="programme-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--hi-orange)" }}>FUNDING SUBMISSION</div>
        <h1 className="font-heading font-black text-4xl mt-2 tracking-tight" style={{ color: "var(--hi-olive)" }}>
          Retrofit programme<br />
          <span style={{ color: "var(--hi-orange)" }}>bid pack.</span>
        </h1>
        <p className="text-muted-foreground mt-3 max-w-3xl leading-relaxed">
          Build SHDF / Warm Homes Fund-ready PDFs. Run a portfolio-wide bid pack across your top-priority properties,
          or zoom into a single property and edit its programme line-by-line. SAP uplifts are RdSAP-10 aligned.
        </p>
      </div>

      <CrossCheckCard />

      <Tabs defaultValue="portfolio" className="w-full">
        <TabsList className="bg-transparent border-b rounded-none p-0 h-auto" style={{ borderColor: "var(--hi-border)" }} data-testid="programme-tabs">
          <TabsTrigger value="portfolio" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:shadow-none px-4 py-3" style={{ borderColor: "var(--hi-olive)" }} data-testid="tab-portfolio">
            <FileText className="h-3.5 w-3.5 mr-1.5" />Portfolio bid pack
          </TabsTrigger>
          <TabsTrigger value="single" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:shadow-none px-4 py-3" style={{ borderColor: "var(--hi-olive)" }} data-testid="tab-single">
            <Target className="h-3.5 w-3.5 mr-1.5" />Single property
          </TabsTrigger>
        </TabsList>

        <TabsContent value="portfolio" className="space-y-6 mt-6">
          <Card className="rounded-none border-border p-6" data-testid="programme-builder">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 block">Scope</label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="rounded-none border-slate-300 h-11" data-testid="scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    <div>
                      <div className="font-medium">
                        {s.label}
                        {counts[s.value] != null && <span className="ml-2 font-mono text-xs text-muted-foreground" data-testid={`scope-count-${s.value}`}>({counts[s.value].toLocaleString("en-GB")})</span>}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{s.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 block">Top N properties (1-2000)</label>
            <Input
              type="number" min={1} max={2000}
              value={topN}
              onChange={(e) => setTopN(Math.max(1, Math.min(2000, Number(e.target.value) || 50)))}
              className="rounded-none border-slate-300 h-11 font-mono"
              data-testid="top-n"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Ranked by retrofit priority score · highest first
              {counts[scope] != null && (
                <button type="button" onClick={() => setTopN(Math.min(2000, counts[scope]))} className="ml-2 underline font-semibold" data-testid="top-n-all">
                  Use all {counts[scope].toLocaleString("en-GB")}
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col justify-end gap-2">
            <Button
              size="lg"
              onClick={generate}
              disabled={generating || generatingCsv}
              className="rounded-none h-11 text-white"
              style={{ backgroundColor: "var(--hi-olive)" }}
              data-testid="generate-programme"
            >
              {generating
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <FileText className="h-4 w-4 mr-2" />}
              {generating ? "Building PDF…" : "Generate PDF"}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={generateCsv}
              disabled={generating || generatingCsv}
              className="rounded-none h-11 border-2"
              style={{ borderColor: "var(--hi-olive)", color: "var(--hi-olive)" }}
              data-testid="generate-programme-csv"
            >
              {generatingCsv
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              {generatingCsv ? "Building CSV…" : "Export CSV"}
            </Button>
          </div>
        </div>
      </Card>

      {preview && (
        <Card className="rounded-none p-6" style={{ backgroundColor: "#fff8ed", borderColor: "var(--hi-orange)", border: "2px solid" }} data-testid="programme-preview">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold mb-3" style={{ color: "var(--hi-orange-deep)" }}>
            <Download className="h-3.5 w-3.5" /> Programme generated
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Properties</div>
              <div className="font-heading font-black text-3xl mt-1" style={{ color: "var(--hi-orange-deep)" }}>{preview.propCount.toLocaleString("en-GB")}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{preview.portfolioTotal != null ? "Indicative total" : "Format"}</div>
              <div className="font-heading font-black text-3xl mt-1" style={{ color: "var(--hi-orange-deep)" }}>{preview.portfolioTotal != null ? GBP(preview.portfolioTotal) : preview.kind?.toUpperCase()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">File size</div>
              <div className="font-heading font-black text-3xl mt-1" style={{ color: "var(--hi-orange-deep)" }}>{Math.round(preview.size / 1024)} KB</div>
            </div>
          </div>
        </Card>
      )}

      <Card className="rounded-none border-dashed border-slate-300 p-4 text-xs text-slate-600 leading-relaxed flex items-start gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--hi-orange)" }} />
        <span>
          <strong>How the planner works.</strong> For every property in scope we pull its gov-recommended measures (if synced) and apply
          our RdSAP-10-aligned SAP-uplift lookup. A greedy cost-per-SAP-point algorithm then selects the minimum subset of measures
          needed to lift the dwelling from its current SAP to ≥ 69 (band C). The resulting cost is indicative — a PAS 2035 assessor
          will reconcile final figures on site.
        </span>
      </Card>
        </TabsContent>

        <TabsContent value="single" className="mt-6">
          <SinglePropertyProgramme />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// Single-property programme — search, view the path-to-C plan, manually
// edit measures (add / delete), and export a per-property PDF.
// ============================================================================
function SinglePropertyProgramme() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null); // {id, address, ...}
  const [plan, setPlan] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [heritage, setHeritage] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newM, setNewM] = useState({ label: "", cost: "" });
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState({ pdf: false, csv: false });

  const exportProperty = async (kind /* 'pdf' | 'csv' */) => {
    if (!selected) return;
    setExporting((e) => ({ ...e, [kind]: true }));
    try {
      const token = localStorage.getItem("epc_token");
      const res = await fetch(
        `${API_BASE}/reports/property/${selected.id}/programme.${kind}`,
        { headers: { Authorization: `Bearer ${token}` }, credentials: "include" },
      );
      if (!res.ok) {
        const body = res.headers.get("content-type")?.includes("json")
          ? await res.json().catch(() => ({}))
          : { detail: await res.text() };
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (selected.address || "property").replace(/[, ]+/g, "-").slice(0, 60);
      a.href = url;
      a.download = `Retrofit-${safe}-${new Date().toISOString().slice(0, 10)}.${kind}`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 500);
      toast.success(`${kind.toUpperCase()} downloaded · ${Math.round(blob.size / 1024)} KB`);
    } catch (e) {
      toast.error(`Failed to generate ${kind.toUpperCase()}`, { description: e.message });
    } finally {
      setExporting((e) => ({ ...e, [kind]: false }));
    }
  };

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/properties", { params: { search: query, page_size: 10 } });
        setResults(data.items || []);
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const loadProperty = async (p) => {
    setSelected(p);
    setLoadingDetail(true);
    setPlan(null); setEstimate(null); setHeritage(null);
    try {
      const [planR, estR, herR] = await Promise.all([
        api.get(`/properties/${p.id}/path-to-c`),
        api.get(`/properties/${p.id}/cost-estimate`),
        // Auto-run heritage on first load — uses postcodes.io geocode
        // fallback so it works even when the property has no lat/lng yet
        api.get(`/properties/${p.id}/heritage`, { params: { refresh: true } }),
      ]);
      setPlan(planR.data);
      setEstimate(estR.data);
      setHeritage(herR.data);
    } catch (e) {
      toast.error("Failed to load property details", { description: e?.response?.data?.detail || e.message });
    } finally { setLoadingDetail(false); }
  };

  const addMeasure = async () => {
    if (!selected) return;
    const label = newM.label.trim();
    const cost = Number(newM.cost);
    if (label.length < 2 || !Number.isFinite(cost) || cost < 1000 || cost > 10000) {
      toast.error("Label ≥ 2 chars, cost £1,000–£10,000");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${selected.id}/measures`, { label, cost });
      setEstimate(data);
      setNewM({ label: "", cost: "" });
      toast.success("Measure added");
    } catch (e) {
      toast.error("Add failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const removeMeasure = async (mid) => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/properties/${selected.id}/measures/${mid}`);
      setEstimate(data);
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const refreshHeritage = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/properties/${selected.id}/heritage`, { params: { refresh: true } });
      setHeritage(data);
      if (data?.reason === "no_location") {
        toast.error("Could not geocode this property", { description: data.message || "Add a postcode to enable heritage lookup" });
      } else if (data?.has_constraints) {
        toast.warning(`${data.constraints.length} heritage constraint(s) found`, {
          description: data.constraints.map((c) => c.label).join(" · "),
        });
      } else {
        toast.success("No heritage constraints at this location ✓");
      }
    } catch (e) {
      toast.error("Heritage lookup failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const SEVERITY_STYLE = {
    block: { bg: "#fef2f2", border: "#dc2626", color: "#7f1d1d" },
    warn: { bg: "#fff8ed", border: "var(--hi-orange)", color: "var(--hi-orange-deep)" },
  };

  return (
    <div className="space-y-6" data-testid="single-property-builder">
      <Card className="rounded-none border-border p-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search any property by address, postcode or UPRN…"
            className="rounded-none border-slate-300 h-11 pl-10"
            data-testid="single-property-search"
          />
          {searching && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
        </div>
        {results.length > 0 && (
          <div className="mt-3 border border-slate-200 max-h-64 overflow-auto">
            {results.map((p) => (
              <button
                key={p.id}
                onClick={() => { loadProperty(p); setResults([]); setQuery(p.address); }}
                className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-slate-100 last:border-0 flex items-center justify-between gap-3"
                data-testid={`search-result-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <EPCBadge band={p.current_epc?.band} size="sm" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{p.address}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{p.postcode} · UPRN {p.uprn}</div>
                  </div>
                </div>
                <Plus className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </Card>

      {!selected && (
        <Card className="rounded-none border-dashed border-slate-300 p-12 text-center text-sm text-muted-foreground" data-testid="single-property-empty">
          <Building2 className="h-8 w-8 mx-auto mb-3 text-slate-300" />
          Start by searching for a property above to build its retrofit programme.
        </Card>
      )}

      {selected && loadingDetail && <Skeleton className="h-96" />}

      {selected && !loadingDetail && plan && (
        <div className="space-y-6" data-testid="single-property-detail">
          {/* Header card */}
          <Card className="rounded-none p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-center" style={{ borderColor: "var(--hi-olive)", borderWidth: 2 }}>
            <div className="md:col-span-2">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Selected property</div>
              <div className="font-heading font-black text-xl mt-1" style={{ color: "var(--hi-olive)" }}>{selected.address}</div>
              <div className="text-xs font-mono text-muted-foreground mt-1">{selected.postcode} · UPRN {selected.uprn} · {selected.property_type || "—"}</div>
              {/* Heritage badges */}
              {heritage?.constraints && heritage.constraints.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="heritage-badges">
                  {heritage.constraints.map((c, i) => {
                    const s = SEVERITY_STYLE[c.severity] || SEVERITY_STYLE.warn;
                    return (
                      <span key={i} title={c.name}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border"
                            style={{ backgroundColor: s.bg, color: s.color, borderColor: s.border }}
                            data-testid={`heritage-tag-${c.dataset}`}>
                        {c.label}
                      </span>
                    );
                  })}
                </div>
              ) : heritage?.ok ? (
                <div className="text-[11px] text-emerald-700 mt-3" data-testid="heritage-clear">
                  ✓ No planning / heritage constraints at this location
                  {heritage.checked_at && (
                    <span className="text-muted-foreground ml-1">· checked {new Date(heritage.checked_at).toLocaleDateString("en-GB")}</span>
                  )}
                </div>
              ) : heritage?.reason === "no_location" ? (
                <div className="text-[11px] text-amber-700 mt-3" data-testid="heritage-no-location">
                  ⚠ Couldn't geocode this property — {heritage.message || "postcode missing or unknown"}
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground mt-3">Heritage data not yet checked</div>
              )}
            </div>
            <div className="text-right flex flex-col gap-1.5 items-end">
              <Button size="sm" variant="outline" onClick={refreshHeritage} disabled={busy} className="rounded-none border-slate-300" data-testid="refresh-heritage">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5 mr-1.5" />}
                Re-check heritage
              </Button>
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  onClick={() => exportProperty("pdf")}
                  disabled={exporting.pdf || exporting.csv || !plan?.ok}
                  className="rounded-none text-white"
                  style={{ backgroundColor: "var(--hi-olive)" }}
                  data-testid="single-export-pdf"
                >
                  {exporting.pdf ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />}
                  PDF
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => exportProperty("csv")}
                  disabled={exporting.pdf || exporting.csv || !plan?.ok}
                  className="rounded-none border-2"
                  style={{ borderColor: "var(--hi-olive)", color: "var(--hi-olive)" }}
                  data-testid="single-export-csv"
                >
                  {exporting.csv ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                  CSV
                </Button>
              </div>
            </div>
          </Card>

          {/* Path-to-C summary */}
          {plan.ok && !plan.already_at_c && (
            <Card className="rounded-none p-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-center" style={{ backgroundColor: "var(--hi-cream)" }}>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Today</div>
                <div className="mt-1"><EPCBadge band={plan.current_band} size="xl" /></div>
                <div className="text-[10px] font-mono mt-1">SAP {plan.current_sap}{plan.current_sap_derived && <span className="text-amber-700">*</span>}</div>
              </div>
              <div className="text-center text-3xl text-slate-300">→</div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Target</div>
                <div className="mt-1"><EPCBadge band={plan.projected_band} size="xl" /></div>
                <div className="text-[10px] font-mono mt-1">SAP {plan.projected_sap}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Path-to-C cost</div>
                <div className="font-heading font-black text-3xl mt-1" style={{ color: "var(--hi-olive)" }}>
                  £{(plan.total_cost || 0).toLocaleString("en-GB")}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">{plan.measures.length} measures</div>
              </div>
            </Card>
          )}
          {plan.ok && plan.already_at_c && (
            <Card className="rounded-none p-6 flex items-center gap-3" style={{ backgroundColor: "#f0fdf4", border: "1px solid #16a34a" }}>
              <Target className="h-6 w-6 text-emerald-700" />
              <div>
                <div className="font-bold text-emerald-900">Already at Band {plan.current_band} (SAP {plan.current_sap})</div>
                <div className="text-xs text-emerald-800/80">No programme required — already at or above the C target.</div>
              </div>
            </Card>
          )}

          {/* Programme editor */}
          {estimate?.has_certificate_detail !== false && (
            <Card className="rounded-none border-border p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
                <PoundSterling className="h-3.5 w-3.5" /> Programme line items
              </div>
              <div className="border border-slate-200" data-testid="programme-measures-table">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-slate-50 border-b border-slate-200">
                  <div className="col-span-7">Measure</div>
                  <div className="col-span-4 text-right">Cost</div>
                  <div className="col-span-1" />
                </div>
                {(estimate?.measures || []).map((m) => (
                  <div key={m.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 items-center text-sm">
                    <div className="col-span-7">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{m.label}</span>
                        {m.kind === "custom" && (
                          <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold" style={{ backgroundColor: "var(--hi-orange)", color: "white" }}>
                            {m.source === "job_card" ? "Job card" : "Custom"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-4 text-right font-mono">£{Math.round(m.applied_cost || 0).toLocaleString("en-GB")}</div>
                    <div className="col-span-1 flex justify-end">
                      <Button size="icon" variant="ghost" disabled={busy} onClick={() => removeMeasure(m.id)} className="h-7 w-7 text-slate-400 hover:text-red-700" data-testid={`single-delete-${m.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
                {/* Add row */}
                <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 items-center text-sm bg-amber-50/30">
                  <div className="col-span-7">
                    <Input value={newM.label} onChange={(e) => setNewM((s) => ({ ...s, label: e.target.value }))} placeholder="Add a measure…" className="rounded-none border-slate-300 h-8 text-xs" data-testid="single-new-label" />
                  </div>
                  <div className="col-span-4">
                    <div className="relative">
                      <PoundSterling className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input type="number" min={1000} max={10000} step={50} value={newM.cost} onChange={(e) => setNewM((s) => ({ ...s, cost: e.target.value }))} placeholder="cost" className="rounded-none border-slate-300 h-8 pl-6 text-right font-mono text-xs" data-testid="single-new-cost" />
                    </div>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button size="icon" variant="outline" disabled={busy} onClick={addMeasure} className="h-7 w-7 rounded-none border-slate-300" data-testid="single-add-btn">
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
                {/* Total */}
                <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-slate-50 border-t border-slate-200 items-center">
                  <div className="col-span-7 text-xs font-semibold uppercase tracking-wider">Property programme total</div>
                  <div className="col-span-4 text-right font-mono text-base font-bold" data-testid="single-total">£{Math.round(estimate?.totals?.applied_cost || 0).toLocaleString("en-GB")}</div>
                  <div className="col-span-1" />
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
